import type { ShowDocumentDetailsFn } from '@/features/documents'
import { action, computed } from 'mobx'
import { type Job } from '@/features/jobs/Job'
import { type Document } from './Document'
import {
  archiveManagedDocuments,
  type DocumentManagementActionContext,
  downloadManagedDocuments,
} from './document-management-actions'

/**
 * View model for a document in the document management table.
 */
export class DocumentManagementRowViewModel {
  constructor(
    readonly document: Document,
    private readonly _getLinkedJobs: (document: Document) => Job[],
    private readonly _viewDocumentDetails: ShowDocumentDetailsFn,
    private readonly _actionContext: DocumentManagementActionContext,
  ) {
    this.download = this.download.bind(this)
    this.showDetails = this.showDetails.bind(this)
    this.delete = this.delete.bind(this)
  }

  /**
   * Whether the document is selected.
   */
  @computed
  get isSelected() {
    return this._actionContext.selection.has(this.document.id)
  }

  /**
   * The loaded jobs the document is linked to.
   */
  @computed
  get linkedJobs() {
    return this._getLinkedJobs(this.document)
  }

  /**
   * Selects or deselects the document.
   */
  @action.bound
  toggleSelected(checked: boolean) {
    if (checked) {
      this._actionContext.selection.add(this.document.id)
    } else {
      this._actionContext.selection.delete(this.document.id)
    }
  }

  /**
   * Downloads the document.
   */
  download() {
    return downloadManagedDocuments(this._actionContext, [this.document])
  }

  /**
   * Shows the document's details with admin rights.
   */
  showDetails() {
    this._viewDocumentDetails(this.document, true)
  }

  /**
   * Deletes the document after confirmation.
   */
  delete() {
    return archiveManagedDocuments(this._actionContext, [this.document])
  }
}
